<template>
  <div
    class="diff-modal-side-inner-container"
    :data-testid="revision.isCurrent ? 'diff-modal-side-current' : 'diff-modal-side-historical'"
  >
    <div class="revision-creation-and-action">
      <div class="revision-creation">
        <div class="score">
          <ScoreMeter
            animate
            icon
            :score="startScoreAnimation ? revision.score : 0"
          />
        </div>
        <div class="revision-creation-person-and-time">
          <p class="p5">
            {{ createdData.createdBy }}
          </p>
          <p>
            {{ createdData.created }}
          </p>
        </div>
      </div>
      <DpplAutoSizedButton
        v-if="revision && !revision.isCurrent"
        :loading="loading"
        :disabled="loading || disableButtons"
        class="restore-button"
        @click="$emit('revertToRevision')"
      >
        Restore
      </DpplAutoSizedButton>
    </div>
    <div class="revision-content">
      <!-- eslint-disable -->
      <h4
        class="revision-title"
      >
        <DpplSafeHtml :html="revision.diffedTitle" />
      </h4>
      <!-- eslint-enable -->
      <div
        class="revision-company-and-location p2 mb-6"
      >
        <DpplSafeHtml :html="companyAndLocationDiff" />
      </div>
      <div
        v-for="field in revision.fields"
        :key="field.id + (revision.isCurrent ? '-current' : '')"
        class="revision-field"
      >
        <div
          class="revision-field-label my-4"
        >
          <FieldLabel>{{ field.label }}</FieldLabel>
        </div>
        <div
          v-if="field.hasText"
          class="revision-field-html"
        >
          <DpplSafeHtml :html="field.diffedHTML" />
        </div>
        <div
          v-else
          data-testid="empty-field"
        />
      </div>
    </div>
  </div>
</template>

<script>
import ScoreMeter from '@/components/editor/ScoreMeter.vue';
import DpplAutoSizedButton from '@/components/datapeople/DpplAutoSizedButton.vue';
import DpplSafeHtml from '@ui/components/general/DpplSafeHtml/DpplSafeHtml.vue';
import FieldLabel from '@/components/editor/field/FieldLabel.vue';

export default {
  name: 'DiffRevision',
  components: {
    DpplSafeHtml,
    ScoreMeter,
    DpplAutoSizedButton,
    FieldLabel,
  },
  props: {
    revision: {
      type: Object,
      required: true,
    },
    startScoreAnimation: {
      type: Boolean,
      default: false,
    },
    createdData: {
      type: Object,
      required: true,
    },
    disableButtons: {
      type: Boolean,
      default: false,
    },
    loading: {
      type: Boolean,
      default: true,
    },
    showText: {
      type: Object,
      default() {
        return {
          showLocation: false,
          showReqId: false,
        };
      },
    },
  },
  emits: ['revertToRevision'],
  computed: {
    companyAndLocationDiff() {
      let htmlDiff = this.revision.diffedCompany;
      if (this.showText.showLocation) {
        htmlDiff += ` - ${this.revision.diffedLocation}`;
      }

      if (this.showText.showReqId) {
        htmlDiff += ` - ${this.revision.diffedReqId}`;
      }
      return htmlDiff;
    },
  },
};
</script>

<style lang="less" scoped>
.restore-button {
  letter-spacing: normal;
  font-weight: 600 !important;
}

.diff-modal-side-inner-container {
  width: 100%;
  height: 100%;
  padding: 93px 0px 25px 0px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
}

.revision-creation-and-action {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0px 25px 25px 0px;
  border-bottom: 1px solid @stroke-default;
}

.revision-creation {
  display: flex;
  align-items: flex-start;
  justify-content: flex-start;
}

.score {
  width: 90px;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.revision-creation-person-and-time {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;

  p {
    text-align: left;
  }
}

.revision-content {
  padding: 25px 25px 0px 25px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  max-height: calc(90vh - 156px);
  overflow-y: auto;
  width: 100%;
}

.revision-company-and-location {
  text-align: left;

  :deep(.removed-diff) {
    background: @bg-error-light !important;
  }

  :deep(.added-diff) {
    background: @green-200 !important;
  }
}

.revision-field {
  width: 100%;
}

.revision-field-label {
  width: 100%;
  border-bottom: 1px solid @stroke-default;
  position: relative;

  :deep(.field-label) {
    position: absolute;
    right: 0px;
    bottom: 0px;

    .text {
      background: @grey-400;
    }

    .icon {
      fill: @grey-400;
    }
  }
}

.revision-title {
  text-align: left;

  :deep(.removed-diff) {
    background: @bg-error-light !important;
  }

  :deep(.added-diff) {
    background: @green-200 !important;
  }
}

.revision-field-html {
  text-align: left;

    &:deep(h1) {
      font-size: 24px;
      line-height: 28px;
      font-weight: 700;
      margin-bottom: 24px;
    }
    &:deep(h2) {
      font-size: 20px;
      line-height: 28px;
      font-weight: 700;
      margin-bottom: 24px;
      }
    &:deep(h3) {
      font-size: 18px;
      line-height: 28px;
      font-weight: 700;
      margin-bottom: 24px;
    }

    &:deep(p), &:deep(section), &:deep(article) {
      font-size: 16px;
      line-height: 28px;
      font-weight: 300;
      margin-bottom: 24px;
    }

    &:deep(span) {
      font-size: 16px;
      line-height: 28px;
      font-weight: 300;
    }

    &:deep(strong), &:deep(b) {
      font-weight: 700;
    }

    &:deep(ul), &:deep(ol) {
      margin-bottom: 24px;

      li {
        font-size: 16px;
        line-height: 28px;
        font-weight: 300;
      }
    }

    &:deep(.removed-diff) {
      background: @bg-error-light !important;
    }

    &:deep(.added-diff) {
      background: @green-200 !important;
    }
}
</style>
